import React from 'react';
import { useTranslation } from 'react-i18next';
import { DialogProps } from '@mui/material/Dialog';
import ZModal from '../../../../components/ZModal';
import { useZModal } from '../../../../components/ZModal/use';
import { Loader, ZigButton, ZigTypography } from '@zignaly-open/ui';
import { Box } from '@mui/material';
import { useCanDeposit } from '../../../../util/walls/util';
import {
  useKycStatusesQuery,
  useUserIpInfoQuery,
} from '../../../../apis/user/api';
import { extractStatus } from '../../../../apis/user/util';
import { KycStatus } from '../../../../apis/user/types';
import PaybisBuyForm from './forms/Paybis/PaybisBuyForm';
import { getKycStatusMessage } from '../../../Settings/EditProfile/atoms';
import { useOpenUserKyc } from '../../../Settings/Kyc/use';

function BuyModal(props: DialogProps): React.ReactElement {
  const { t } = useTranslation(['deposit-crypto', 'settings', 'kyc']);

  const { data: kycData, isLoading, refetch } = useKycStatusesQuery('sumsub');
  const { isLoading: isLoadingIpInfo } = useUserIpInfoQuery();
  const kycStatus = extractStatus(kycData);

  const { openKyc, loadingLink } = useOpenUserKyc();

  return (
    <ZModal mobileFullScreen wide {...props} title={t('buy-crypto')}>
      {isLoading || !kycData || isLoadingIpInfo ? (
        <Box sx={{ p: 4, justifyContent: 'center', display: 'flex' }}>
          <Loader />
        </Box>
      ) : kycStatus === KycStatus.APPROVED ? (
        <PaybisBuyForm />
      ) : kycStatus === KycStatus.NOT_STARTED ? (
        <Box sx={{ textAlign: 'center' }}>
          <ZigTypography variant={'body1'} component={'p'}>
            {t('buy-crypto-modal.kyc-needed-description')}
          </ZigTypography>

          <ZigButton
            sx={{ mt: 4 }}
            variant={'contained'}
            size={'large'}
            onClick={() => {
              openKyc('KYC', 1);
              setTimeout(refetch, 1000);
            }}
            loading={loadingLink}
          >
            {t('buy-crypto-modal.pass-kyc-cta')}
          </ZigButton>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center' }}>
          <ZigTypography variant={'body1'} component={'p'}>
            {t('buy-crypto-modal.kyc-needed-description')}
          </ZigTypography>

          <ZigTypography variant={'body1'} component={'p'} sx={{ mt: 2 }}>
            {t('buy-crypto-modal.kyc-status', {
              status: t(getKycStatusMessage(kycStatus)),
            })}

            {!!kycData?.statuses?.[0]?.reason &&
              t('buy-crypto-modal.kyc-status-reason', {
                reason: kycData?.statuses?.[0]?.reason,
              })}
          </ZigTypography>

          {kycStatus === KycStatus.REJECTED_RETRY && (
            <ZigButton
              sx={{ mt: 4 }}
              variant={'contained'}
              size={'large'}
              onClick={() => openKyc('KYC', 1)}
              loading={loadingLink}
            >
              {t('buy-crypto-modal.pass-kyc-cta-retry')}
            </ZigButton>
          )}
        </Box>
      )}
    </ZModal>
  );
}

BuyModal.trackId = 'buy';

export const useOpenBuyModal = (): ((props?: Partial<DialogProps>) => void) => {
  const checkCanDeposit = useCanDeposit();
  const { showModal } = useZModal({ disableAutoDestroy: true });
  return (props) => {
    checkCanDeposit() && showModal(BuyModal, props);
  };
};

export default BuyModal;
